import { Logger } from '~~/helpers/logger';
import { useCartApi } from '~/utils/ct/composables/api/useCartApi';
import { errorToString } from '~~/helpers/error/errorToString';
import type { Cart, CustomQuery, LineItem, MyCartUpdateAction, ProductVariant } from '~~/types/commercetools';
import { isPostalCodeCartError } from '~~/types/checkout/PostalCodeCartError';
import type { PostalCodeCartError } from '~~/types/checkout/PostalCodeCartError';

export function useCart() {

  const { countryCode } = useLocalization();
  const {
    load: apiLoad,
    addItem: apiAddItem,
    removeItem: apiRemoveItem,
    updateItemQty: apiUpdateItemQty,
    clear: apiClear,
    applyCoupon: apiApplyCoupon,
    removeCoupon: apiRemoveCoupon,
    updateCart: apiUpdateCart,
    replicateCart: apiReplicateCart,
    buildDeleteCartPayload,
  } = useCartApi(countryCode.value);

  const cart = useState<Maybe<Cart>>('useCart-cart', () => null);
  const loading = useState<boolean>('useCart-loading', () => false);
  const error = useState<{
    load: string | null;
    addItem: string| null ;
    removeItem: string | null ;
    updateItemQty: string | null ;
    clear: string | null ;
    applyCoupon: string | null ;
    removeCoupon: string | null ;
    updateCart: string | null ;
    replicateCart: string | null ;
    updateCartPostalCodeError: PostalCodeCartError | null;
  }>('useCart-error', () => ({
    load: null,
    addItem: null,
    removeItem: null,
    updateItemQty: null,
    clear: null,
    applyCoupon: null,
    removeCoupon: null,
    updateCart: null,
    replicateCart: null,
    updateCartPostalCodeError: null,
  }));

  async function load(params: { customQuery?: CustomQuery }) {

    Logger.debug('useCart.load');
    try {

      loading.value = true;
      const loadedCart = await apiLoad(params);
      cart.value = loadedCart;
      error.value.load = null;
    
    } catch (err) {

      error.value.load = errorToString(err);
      Logger.error('useCart.load', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  function setCart(newCart: Cart | null) {

    cart.value = newCart;
    Logger.debug('useCartFactory.setCart', newCart);
  
  }

  async function replicateCart(parentOrderNumber: string) {

    Logger.debug('useCart.replicateCart');
    try {

      loading.value = true;
      const newCart = await apiReplicateCart(parentOrderNumber);
      cart.value = newCart;
      error.value.replicateCart = null;
    
    } catch (err) {

      error.value.replicateCart = errorToString(err);
      Logger.error('useCart.replicateCart', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function updateCart({
    id,
    version,
    actions,
  }: {
    id: string;
    version: string;
    actions: MyCartUpdateAction[];
  }, customQuery: CustomQuery) {

    if (cart.value) {

      Logger.debug('useCart.updateCart');
      try {

        loading.value = true;
        const updatedCart = await apiUpdateCart({ id, version, actions, customQuery });
        cart.value = updatedCart;
        error.value.updateCart = null;
        error.value.updateCartPostalCodeError = null;

        if (updatedCart) {

          setCart(updatedCart);

          return;

        }

      } catch (err) {

        error.value.updateCart = errorToString(err);
        error.value.updateCartPostalCodeError = isPostalCodeCartError(err) ? err : null;
        Logger.error('useCart.updateCart', err);
      
      } finally {

        loading.value = false;
      
      }
    
    }
  
  }

  async function addItem(params: {
    product: ProductVariant;
    quantity: number;
    triggeredIn?: string;
    customQuery: CustomQuery;
}) {

    Logger.debug('useCart.addItem');
    try {

      loading.value = true;
      const updatedCart = await apiAddItem({ currentCart: cart.value, ...params });
      cart.value = updatedCart;
      error.value.addItem = null;
    
    } catch (err) {

      error.value.addItem = errorToString(err);
      Logger.error('useCart.addItem', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function removeItem(params: { product: LineItem, customQuery: CustomQuery }) {

    Logger.debug('useCart.removeItem');
    try {

      loading.value = true;
      const updatedCart = await apiRemoveItem({ currentCart: cart.value, ...params });
      cart.value = updatedCart;
      error.value.removeItem = null;
    
    } catch (err) {

      error.value.removeItem = errorToString(err);
      Logger.error('useCart.removeItem', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function updateItemQty(params: {
    currentCart?: Cart;
    product: LineItem;
    quantity?: number;
    customQuery?: CustomQuery;
}) {

    Logger.debug('useCart.updateItemQty');
    try {

      loading.value = true;
      const updatedCart = await apiUpdateItemQty({ currentCart: cart.value, ...params });
      cart.value = updatedCart;
      error.value.updateItemQty = null;
    
    } catch (err) {

      error.value.updateItemQty = errorToString(err);
      Logger.error('useCart.updateItemQty', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function clear() {

    Logger.debug('useCart.clear');
    try {

      loading.value = true;
      await apiClear({ currentCart: cart.value });
      cart.value = null;
      error.value.clear = null;
    
    } catch (err) {

      error.value.clear = errorToString(err);
      Logger.error('useCart.clear', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function applyCoupon(params: { couponCode: string, customQuery?: CustomQuery }) {

    Logger.debug('useCart.applyCoupon');
    try {

      loading.value = true;
      const result = await apiApplyCoupon({ currentCart: cart.value, ...params });
      cart.value = result.updatedCart;
      error.value.applyCoupon = null;
    
    } catch (err) {

      error.value.applyCoupon = errorToString(err);
      Logger.error('useCart.applyCoupon', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function removeCoupon(params: { couponCode: string, customQuery?: CustomQuery }) {

    Logger.debug('useCart.removeCoupon');
    try {

      loading.value = true;
      const result = await apiRemoveCoupon({ currentCart: cart.value, ...params });
      cart.value = result.updatedCart;
      error.value.removeCoupon = null;
    
    } catch (err) {

      error.value.removeCoupon = errorToString(err);
      Logger.error('useCart.removeCoupon', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  return {
    load,
    replicateCart,
    setCart,
    addItem,
    removeItem,
    updateItemQty,
    clear,
    applyCoupon,
    removeCoupon,
    updateCart,
    buildDeleteCartPayload,
    cart: computed(() => cart.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };

}
